import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Card, CardContent, Chip, IconButton, Tooltip,
    Select, MenuItem, FormControl, InputLabel, Grid, Collapse, Alert,
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Snackbar, TablePagination
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    CalendarToday as CalendarIcon,
    AccessTime as HoursIcon,
    Work as ProjectIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    EventNote as EmptyIcon,
} from '@mui/icons-material';
import TimesheetDetails from './timesheetDetails';
import { deleteData, getData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import { getUserData } from '../../../utils/auth';
import Loader from '../../../common/spinner';

const cap = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

const STATUS_STYLES = {
    DRAFT:      { bg: '#f1f5f9', color: '#64748b' },
    SUBMITTED:  { bg: '#e3f2fd', color: '#1976d2' },
    APPROVED:   { bg: '#E0F2F1', color: '#00796B' },
    REJECTED:   { bg: '#FFEBEE', color: '#D32F2F' },
};

const MONTHS = ['All', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const YEARS = [];
for(let year = 2024; year <= new Date().getFullYear(); year++){
    YEARS.push(year.toString());
}
const STATUSES = ['All', 'DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED'];
const SORT_FIELDS = [
    { value: 'week_start_date', order: 'ASC', label: 'Week (oldest to newest)' },
    { value: 'week_start_date', order: 'DESC', label: 'Week (newest to oldest)' },
    { value: 'total_hours', order: 'ASC', label: 'Total Hours (low to high)' },
    { value: 'total_hours', order: 'DESC', label: 'Total Hours (high to low)' },
    { value: 'updatedAt', order: 'DESC', label: 'Last Updated' },
].map((opt) => ({ ...opt, key: `${opt.value}_${opt.order}` }));

function formatStatus(string) {
    if (!string) return '';
    return string
        .toString()
        .toLowerCase()
        .split(/[_\s-]+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function formatDateRange(start, end) {
    const s = new Date(start);
    const e = new Date(end);
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[s.getMonth()]} ${s.getDate()} – ${monthNames[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`;
}

function TimesheetList({ onEdit }) {
    const [year, setYear] = useState(new Date().getFullYear().toString());
    const [month, setMonth] = useState('All');
    const [status, setStatus] = useState('All');
    const [expandedId, setExpandedId] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [detailsDialog, setDetailsDialog] = useState({ open: false, timesheet: null });
    const [timesheets, setTimesheets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0)
    const [sortField, setSortField] = useState('updatedAt');
    const [sortOrder, setSortOrder] = useState('DESC');
    const [sortKey, setSortKey] = useState('updatedAt_DESC');

    useEffect(() => {
        const fetchTimesheets = async () => {
            const userData = getUserData();
            const userId = userData?.id;
            if (!userId) {
                setTimesheets([]);
                setLoading(false);
                return;
            }           
            setLoading(true);
            setError(null);

            const monthParam = month === 'All' ? '' : `${MONTHS.indexOf(month)}`;
            const statusParam = status === 'All' ? '' : status;

            try {
                const res = await getData(API_ENDPOINTS.getAllTimesheets(year, monthParam, statusParam, '', userId, page + 1, rowsPerPage, sortField, sortOrder));
                if (res?.status === 'SUCCESS') {
                    const payload = Array.isArray(res.result?.data) ? res.result.data.map((ts) => ({
                        ...ts,
                        projectCount: projectCount(ts.entries || []),
                    })) : [];
                    setTimesheets(payload);
                    setTotal(res.result?.total || 0)
                } else {
                    setTimesheets([]);
                    setError(res?.message || 'Unable to fetch timesheets');
                }
            } catch (fetchError) {
                setTimesheets([]);
                setTotal(0)
                setError(fetchError?.message || 'Unable to fetch timesheets');
            } finally {
                setLoading(false);
            }
        };

        fetchTimesheets();
    }, [year, month, status, page, rowsPerPage, sortField, sortOrder]);

    const handleFilterChange = (setter) => (e) => {
        setter(e.target.value);
        setPage(0);
    };

    const handleSortFieldChange = (e) => {
        const selected = SORT_FIELDS.find((opt) => opt.key === e.target.value);
        if (selected) {
            setSortField(selected.value);
            setSortOrder(selected.order);
            setSortKey(selected.key);
        }
        setPage(0);
    };

    const projectCount = (entries) => {
        let projects = [];
        entries.length !== 0 &&
            entries.forEach((entry) => {
                if (
                    entry.project_id !== null &&
                    !projects.includes(entry.project_id)
                ) {
                    projects.push(entry.project_id);
                }
            });
        return projects.length;
    };

    const handleDelete = async (id) => {
        // setTimesheets((prev) => prev.filter((ts) => ts.id !== id));
        try {
            const response = await deleteData(API_ENDPOINTS.deleteTimesheet(id))
            if (response.status === "SUCCESS") {
                setSnack({
                    open: true,
                    msg: response?.message,
                    severity: "success",
                });
            } else {
                setSnack({ open: true, msg: response?.message, severity: 'error' }); return;
            }
        } catch (error) {
            setSnack({ open: true, msg: error.response?.data?.message || 'Error while updating details', severity: 'error' }); return;
        }
        setDeleteConfirm(null);
    };

    // Phase 2 — version restore handler
    // const handleRestore = (timesheetId, restoredEntries) => {
    //     setTimesheets((prev) => prev.map((ts) => {
    //         if (ts.id !== timesheetId) return ts;
    //         const newVersion = {
    //             versionNumber: (ts.versionHistory?.length ?? 0) + 1,
    //             editedBy: 'John Doe',
    //             editedAt: 'Just now',
    //             isCurrent: true,
    //             entries: restoredEntries,
    //         };
    //         return {
    //             ...ts,
    //             entries: restoredEntries,
    //             versionHistory: [
    //                 newVersion,
    //                 ...(ts.versionHistory || []).map(v => ({ ...v, isCurrent: false })),
    //             ],
    //         };
    //     }));
    //     setDetailsDialog({ open: false, timesheet: null });
    // };

    if(loading) return <Loader/>

    return (
        <Box>
            {/* Filters */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                    { label: 'Year', value: year, setter: setYear, options: YEARS },
                    { label: 'Month', value: month, setter: setMonth, options: MONTHS },
                    { label: 'Status', value: status, setter: setStatus, options: STATUSES },
                ].map(({ label, value, setter, options }) => (
                    <Grid size={{ xs: 12, sm: 3 }} key={label}>
                        <FormControl fullWidth size="small">
                            <InputLabel>{label}</InputLabel>
                            <Select
                                value={value}
                                label={label}
                                onChange={handleFilterChange(setter)}
                                sx={{ borderRadius: 2, bgcolor: '#fff' }}
                            >
                                {options.map((opt) => (
                                    <MenuItem key={opt} value={opt}>{formatStatus(opt)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                ))}
                <Grid size={{ xs: 12, sm: 3 }} key={'Sort Field'}>
                    <FormControl fullWidth size='small'>
                        <InputLabel>Sort By</InputLabel>
                        <Select
                            value={sortKey}
                            label="Sort By"
                            onChange={handleSortFieldChange}
                            sx={{ borderRadius: 2, bgcolor: '#fff' }}
                        >
                            {SORT_FIELDS.map((opt) => (
                                <MenuItem key={opt.key} value={opt.key}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            {/* Empty State */}
            {timesheets.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <EmptyIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 1 }} />
                    <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 500 }}>
                        No timesheets found
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                        Try adjusting your filters or log a new timesheet.
                    </Typography>
                </Box>
            )}

            {/* Timesheet Cards */}
            {timesheets.map((ts) => {
                const style = STATUS_STYLES[ts.status] || STATUS_STYLES.DRAFT;
                const isExpanded = expandedId === ts.id;

                return (
                    <Card
                        key={ts.id}
                        sx={{
                            mb: 2,
                            borderRadius: 3,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                            border: '1px solid #e2e8f0',
                            transition: 'box-shadow 0.2s',
                            '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.10)' },
                        }}
                    >
                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                {/* Period */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 200 }}>
                                    <Box sx={{ bgcolor: '#eef2ff', borderRadius: 2, p: 1, display: 'flex' }}>
                                        <CalendarIcon sx={{ fontSize: 20, color: '#6366f1' }} />
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 14 }}>
                                            {formatDateRange(ts.week_start_date, ts.week_end_date)}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                                            Week period
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Hours & Projects */}
                                <Box sx={{ display: 'flex', gap: 3 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <HoursIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                        <Typography sx={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>
                                              {ts.total_hours} hrs
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <ProjectIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                        <Typography sx={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>
                                            {ts.projectCount} project{ts.projectCount !== 1 ? 's' : ''}
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Status + Actions */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                                    <Chip
                                        label={formatStatus(ts.status)}
                                        size="small"
                                        sx={{
                                            bgcolor: style.bg,
                                            color: style.color,
                                            fontWeight: 600,
                                            fontSize: 12,
                                            borderRadius: 2,
                                        }}
                                    />
                                    <Tooltip title="View Details">
                                        <IconButton
                                            size="small"
                                            onClick={() => setDetailsDialog({ open: true, timesheet: ts })}
                                            sx={{ color: '#6366f1' }}
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    {(ts.status === 'DRAFT' || ts.status === 'REJECTED') && (
                                        <Tooltip title="Edit">
                                            <IconButton size="small" onClick={() => onEdit(ts)} sx={{ color: '#f59e0b' }}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {ts.status === 'DRAFT' && (
                                        <Tooltip title="Delete">
                                            <IconButton size="small" onClick={() => setDeleteConfirm(ts.id)} sx={{ color: '#ef4444' }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    {ts.status === 'REJECTED' && (
                                        <Tooltip title={isExpanded ? 'Hide comment' : 'Show rejection reason'}>
                                            <IconButton size="small" onClick={() => setExpandedId(isExpanded ? null : ts.id)} sx={{ color: '#D32F2F' }}>
                                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>

                            {/* Rejection comment */}
                            {ts.status === 'REJECTED' && (
                                <Collapse in={isExpanded}>
                                    <Alert
                                        severity="error"
                                        sx={{ mt: 2, borderRadius: 2, fontSize: 13 }}
                                    >
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            Rejected by {ts.rejected_by_name} 
                                        </Typography>
                                        {cap(ts.rejection_reason)}
                                    </Alert>
                                </Collapse>
                            )}
                        </CardContent>
                    </Card>
                );
            })}

            {total > 0 && (
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                    sx={{ mt: 1, borderTop: '1px solid #e2e8f0' }}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, color: '#1e293b' }}>Delete Timesheet?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        This draft timesheet will be permanently deleted. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteConfirm(null)} sx={{ textTransform: 'none', color: '#64748b' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => handleDelete(deleteConfirm)}
                        sx={{ textTransform: 'none', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, borderRadius: 2 }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Details Dialog */}
            <TimesheetDetails
                open={detailsDialog.open}
                timesheet={detailsDialog.timesheet}
                onClose={() => setDetailsDialog({ open: false, timesheet: null })}
                onRestore={() => {}}
            />
            <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnack(p => ({ ...p, open: false }))} severity={snack.severity} sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
            </Snackbar>
        </Box>
    );
}

export default TimesheetList;
